import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as CaixaIcon,
  TrendingUp as EntradaIcon,
  TrendingDown as SaidaIcon,
  List as ListIcon,
  Inventory2 as InventoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  CaixaResumo,
  CategoriaEntradaCaixa,
  CategoriaSaidaCaixa,
  CATEGORIA_ENTRADA_LABEL,
  CATEGORIA_SAIDA_LABEL,
  MovimentacaoCaixa,
  movimentacaoAfetaSaldoCaixa,
} from '../types';
import { cashMovementService } from '../services/caixaService';
import vehicleService from '../services/vehicleService';

/** Converte valor (string/number da API) para número. */
const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

/** Máscara pt-BR: R$ X.XXX,XX */
const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '—';
  const d = String(dateString).split('T')[0];
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const getCategoryLabel = (m: MovimentacaoCaixa): string => {
  if (!m.category) return '—';
  if (m.type === 'entrada') {
    return CATEGORIA_ENTRADA_LABEL[m.category as CategoriaEntradaCaixa] ?? String(m.category);
  }
  return CATEGORIA_SAIDA_LABEL[m.category as CategoriaSaidaCaixa] ?? String(m.category);
};

const kpiCardSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
};

const Caixa: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<CaixaResumo | null>(null);
  const [valorEstoque, setValorEstoque] = useState(0);

  const loadResumo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, vehicles] = await Promise.all([
        cashMovementService.getResumo(),
        vehicleService.getAllVehicles().catch(() => []),
      ]);
      setResumo(data);
      const estoque = (vehicles || [])
        .filter((v: any) => v.status === 'available')
        .reduce((sum: number, v: any) => sum + (v.is_consignado ? 0 : toNum(v.purchase_price)), 0);
      setValorEstoque(estoque);
    } catch (e) {
      console.error(e);
      setError('Não foi possível carregar o caixa.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResumo();
  }, [loadResumo]);

  if (loading && !resumo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const data = resumo || {
    totalVendas: 0,
    receitaRealVendas: 0,
    totalMovimentacoesEntrada: 0,
    totalMovimentacoesSaida: 0,
    totalDespesas: 0,
    totalEntradas: 0,
    totalSaidas: 0,
    lucroCaixa: 0,
    movimentacoes: [],
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Caixa
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>
            Visão consolidada do fluxo de caixa: entradas (lucro real das vendas + movimentações) e saídas (despesas de veículos
            + movimentações debitadas). Vendas a prazo entram proporcionalmente conforme entrada e baixa das parcelas. Contas a pagar
            pendentes não debitam o caixa até a baixa.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadResumo()} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
          <Button variant="outlined" startIcon={<ListIcon />} onClick={() => navigate('/movimentacoes')}>
            Movimentações
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Estoque (custo)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {formatCurrency(valorEstoque)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Soma do preço de compra dos veículos disponíveis (consignados não entram).
                </Typography>
              </Box>
              <InventoryIcon sx={{ color: theme.palette.text.secondary, mt: 0.25 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Entradas
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.palette.primary.main }}>
                  {formatCurrency(data.totalEntradas)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Lucro real recebido (vendas) + entradas de movimentações.
                </Typography>
              </Box>
              <EntradaIcon sx={{ color: theme.palette.primary.main, mt: 0.25 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Saídas
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.palette.error.main }}>
                  {formatCurrency(data.totalSaidas)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Despesas de veículos + saídas de movimentações debitadas.
                </Typography>
              </Box>
              <SaidaIcon sx={{ color: theme.palette.error.main, mt: 0.25 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Resultado (lucro real)
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    color: data.lucroCaixa >= 0 ? theme.palette.success.main : theme.palette.error.main,
                  }}
                >
                  {formatCurrency(data.lucroCaixa)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Total de entradas − total de saídas.
                </Typography>
              </Box>
              <CaixaIcon sx={{ color: theme.palette.text.secondary, mt: 0.25 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Detalhamento do cálculo
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EntradaIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Entradas
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography color="text.secondary">Lucro real recebido (vendas)</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(data.receitaRealVendas)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                    <Typography color="text.secondary">Entradas (movimentações)</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(data.totalMovimentacoesEntrada)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>Total de entradas</Typography>
                    <Typography sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                      {formatCurrency(data.totalEntradas)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SaidaIcon color="error" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Saídas
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography color="text.secondary">Despesas de veículos (não vendidos)</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(data.totalDespesas)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                    <Typography color="text.secondary">Saídas (movimentações debitadas)</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(data.totalMovimentacoesSaida)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>Total de saídas</Typography>
                    <Typography sx={{ fontWeight: 800, color: theme.palette.error.main }}>
                      {formatCurrency(data.totalSaidas)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Movimentações recentes
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Data</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Caixa</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.movimentacoes || []).slice(0, 10).map((m) => {
                    const affects = movimentacaoAfetaSaldoCaixa(m);
                    const pending = m.type === 'saida' && !affects;
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell>{formatDateBR(m.date)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={m.type === 'entrada' ? 'Entrada' : 'Saída'}
                            color={m.type === 'entrada' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {m.type === 'entrada' ? (
                            <Chip size="small" label="Entra" color="success" variant="outlined" />
                          ) : pending ? (
                            <Tooltip title="Conta a pagar pendente (não debita o caixa)">
                              <Chip size="small" label="Pendente" color="warning" variant="outlined" />
                            </Tooltip>
                          ) : (
                            <Chip size="small" label="Debitado" color="default" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>{getCategoryLabel(m)}</TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography variant="body2" noWrap title={m.description}>
                            {m.description}
                          </Typography>
                          {m.payableNote ? (
                            <Typography variant="caption" color="text.secondary" noWrap title={m.payableNote}>
                              Lembrete: {m.payableNote}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: m.type === 'entrada' ? theme.palette.success.main : theme.palette.error.main,
                          }}
                        >
                          {m.type === 'entrada' ? '+' : '−'} {formatCurrency(m.amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(data.movimentacoes || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">Nenhuma movimentação encontrada.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" startIcon={<ListIcon />} onClick={() => navigate('/movimentacoes')}>
                Ver todas as movimentações
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Caixa;
